/**
 * Configure Conditions Tab
 *
 * Allows users to create condition templates at the component, category,
 * or expense type level. These templates define verification sections
 * (groups + questions) that auto-populate when invoices/payments matching
 * those entities are processed.
 *
 * Also surfaces V1 expense type document requirements.
 */

// react
import React, { useContext, useEffect, useMemo, useState } from 'react';

// i18n

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// mui
import {
	alpha,
	Autocomplete,
	Avatar,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Paper,
	Radio,
	RadioGroup,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	CategoryOutlined as IconCategory,
	Delete as IconDelete,
	Description as IconDocument,
	Edit as IconEdit,
	Extension as IconComponent,
	Group as IconGroup,
	Inventory2 as IconExpenseType,
} from '@mui/icons-material';

// types
import type {
	Category,
	Component,
	ExpenseType,
	Condition,
	VerificationSection,
} from '../../../../@types';

// data
import { COLORS, GRADIENTS } from '../../../../data';

// design system
import { cardStyles, radius, shadows, spacing, typography } from '../../../../utils/designSystem';

// reusable condition settings editor
import { ConditionSettingsEditor } from '../../../ui/ConditionSettingsEditor/ConditionSettingsEditor';

// Types

type ConditionLevel = 'component' | 'category' | 'expenseType';

interface ConditionLevelOption {
	icon: React.ReactNode;
	iconColor: string;
	label: string;
	value: ConditionLevel;
}

interface EntityOption {
	id: number;
	label: string;
}

interface DocumentRequirement {
	id: number;
	isPrivate: boolean;
	isRequired: boolean;
	name?: string;
}

// Constants

const LEVEL_OPTIONS: ConditionLevelOption[] = [
	{
		icon: <IconComponent />,
		iconColor: COLORS.primary.main,
		label: 'Component',
		value: 'component',
	},
	{ icon: <IconCategory />, iconColor: COLORS.info.main, label: 'Category', value: 'category' },
	{ icon: <IconExpenseType />, iconColor: '#8b5cf6', label: 'Expense Type', value: 'expenseType' },
];

const LEVEL_LABEL: Record<ConditionLevel, string> = {
	component: 'Component',
	category: 'Category',
	expenseType: 'Expense Type',
};

// Component

export const ConfigureConditions = () => {
	const theme = useTheme();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { createCondition, deactivateCondition, updateCondition, refetchConditions } =
		useContext(ContextSpendPlan);

	// session auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const authHeaders = {
		Authorization: `Bearer ${accessToken}`,
		User: String(idToken),
	};

	// Independent fetch for ALL template conditions (context conditions may be paginated)
	const [templateConditionsAll, setTemplateConditionsAll] = useState<Condition[]>([]);

	const { refetch: refetchTemplates } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setTemplateConditionsAll(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '')}/v2/conditions?project=${project?.id}&populate=component,category,expenseType&orderBy=sequenceOrder&order=ASC`,
	});

	// state
	const [selectedLevel, setSelectedLevel] = useState<ConditionLevel>('expenseType');
	const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);

	// inline add condition form state
	const [isAddingCondition, setIsAddingCondition] = useState(false);
	const [, setNewConditionName] = useState('');
	const [, setNewConditionDescription] = useState('');
	const [newConditionSections, setNewConditionSections] = useState<VerificationSection[]>([]);

	// edit state
	const [editingConditionId, setEditingConditionId] = useState<number | null>(null);
	const [editSections, setEditSections] = useState<VerificationSection[]>([]);

	// delete confirm state
	const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
	const [isDeactivating, setIsDeactivating] = useState(false);

	const [componentsData, setComponentsData] = useState<Component[]>([]);
	const [categoriesData, setCategoriesData] = useState<Category[]>([]);
	const [expenseTypesData, setExpenseTypesData] = useState<ExpenseType[]>([]);

	// Fetch components
	const { isLoading: isLoadingComponents } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setComponentsData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&orderBy=name&order=ASC`,
	});

	// Fetch categories
	const { isLoading: isLoadingCategories } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setCategoriesData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&orderBy=name&order=ASC`,
	});

	// Fetch expense types (with populated documentTypes)
	const { isLoading: isLoadingExpenseTypes } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setExpenseTypesData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&orderBy=name&order=ASC&populate=documentTypes`,
	});

	// Reset entity selection when level changes
	useEffect(() => {
		setSelectedEntityId(null);
		setIsAddingCondition(false);
	}, [selectedLevel]);

	// Reset inline form when entity changes
	useEffect(() => {
		setIsAddingCondition(false);
	}, [selectedEntityId]);

	const handleStartAddCondition = () => {
		setNewConditionName('');
		setNewConditionDescription('');
		setNewConditionSections([]);
		setIsAddingCondition(true);
	};

	const handleCancelAddCondition = () => {
		setIsAddingCondition(false);
	};

	const handleSaveCondition = async () => {
		if (!selectedEntityId || !selectedEntity) return;
		try {
			const res = await createCondition({
				name: `${selectedEntity.label} Requirements`,
				type: 'TEMPLATE',
				conditionType: 'base',
				componentId: selectedLevel === 'component' ? selectedEntityId : null,
				categoryId: selectedLevel === 'category' ? selectedEntityId : null,
				expenseTypeId: selectedLevel === 'expenseType' ? selectedEntityId : null,
				verificationSections: newConditionSections.length > 0 ? newConditionSections : null,
			});
			if (res.error) {
				setBanner({ message: `Failed to save: ${res.error.message}`, severity: 'error' });
			} else {
				setBanner({ message: 'Condition template saved', severity: 'success' });
				setIsAddingCondition(false);
				refetchTemplates();
				refetchConditions();
			}
		} catch {
			setBanner({ message: 'Failed to save condition template', severity: 'error' });
		}
	};

	// Edit handlers
	const handleStartEdit = (condition: Condition) => {
		setEditingConditionId(condition.id);
		setEditSections(condition.verificationSections ? [...condition.verificationSections] : []);
	};

	const handleCancelEdit = () => {
		setEditingConditionId(null);
		setEditSections([]);
	};

	const handleSaveEdit = async () => {
		if (!editingConditionId) return;
		try {
			const res = await updateCondition(editingConditionId, {
				verificationSections: editSections.length > 0 ? editSections : null,
			});
			if (res.error) {
				setBanner({ message: `Failed to update: ${res.error.message}`, severity: 'error' });
			} else {
				setBanner({ message: 'Condition template updated', severity: 'success' });
				setEditingConditionId(null);
				setEditSections([]);
				refetchTemplates();
				refetchConditions();
			}
		} catch {
			setBanner({ message: 'Failed to update condition template', severity: 'error' });
		}
	};

	// Deactivate handlers (soft-delete via PATCH status='DEACTIVATED')
	const handleConfirmDeactivate = async () => {
		if (!deleteConfirmId) return;
		setIsDeactivating(true);
		try {
			const res = await deactivateCondition(deleteConfirmId);
			if (res.error) {
				setBanner({ message: `Failed to deactivate: ${res.error.message}`, severity: 'error' });
			} else {
				setBanner({ message: 'Condition template deactivated', severity: 'success' });
				refetchTemplates();
				refetchConditions();
			}
		} catch {
			setBanner({ message: 'Failed to deactivate condition template', severity: 'error' });
		} finally {
			setIsDeactivating(false);
			setDeleteConfirmId(null);
		}
	};

	// Derive entity options based on selected level
	const entityOptions: EntityOption[] = useMemo(() => {
		switch (selectedLevel) {
			case 'component':
				return componentsData.map((c) => ({ id: c.id, label: c.name }));
			case 'category':
				return categoriesData.map((c) => ({ id: c.id, label: c.name }));
			case 'expenseType':
				return expenseTypesData.map((e) => ({ id: e.id, label: e.name }));
			default:
				return [];
		}
	}, [selectedLevel, componentsData, categoriesData, expenseTypesData]);

	const isLoadingEntities =
		(selectedLevel === 'component' && isLoadingComponents) ||
		(selectedLevel === 'category' && isLoadingCategories) ||
		(selectedLevel === 'expenseType' && isLoadingExpenseTypes);

	// Get selected entity details
	const selectedEntity = entityOptions.find((e) => e.id === selectedEntityId) ?? null;
	const selectedLevelOption =
		LEVEL_OPTIONS.find((l) => l.value === selectedLevel) ?? LEVEL_OPTIONS[0];

	// Get existing condition templates for the selected level (TEMPLATE type only)
	const templateConditions: Condition[] = useMemo(() => {
		if (!selectedEntityId) return [];
		return templateConditionsAll.filter((c) => {
			if (c.type !== 'TEMPLATE') return false;
			switch (selectedLevel) {
				case 'component': {
					const compId =
						typeof c.component === 'number' ? c.component : (c.component as Component)?.id;
					return compId === selectedEntityId;
				}
				case 'category': {
					const catId = typeof c.category === 'number' ? c.category : (c.category as Category)?.id;
					return catId === selectedEntityId;
				}
				case 'expenseType': {
					const etId =
						typeof c.expenseType === 'number' ? c.expenseType : (c.expenseType as ExpenseType)?.id;
					return etId === selectedEntityId;
				}
				default:
					return false;
			}
		});
	}, [templateConditionsAll, selectedLevel, selectedEntityId]);

	// Get document requirements for selected expense type
	const documentRequirements: DocumentRequirement[] = useMemo(() => {
		if (selectedLevel !== 'expenseType' || !selectedEntityId) return [];
		const expenseType = expenseTypesData.find((e) => e.id === selectedEntityId);
		if (!expenseType?.documentTypes) return [];
		return (expenseType.documentTypes as DocumentRequirement[]).filter(
			(dt) => typeof dt === 'object' && dt.id
		);
	}, [selectedLevel, selectedEntityId, expenseTypesData]);

	// Get all templates grouped by level for the overview (TEMPLATE type only)
	const allTemplates = useMemo(() => {
		const templates: {
			condition: Condition;
			entityId: number;
			entityName: string;
			level: ConditionLevel;
		}[] = [];

		templateConditionsAll
			.filter((c) => c.type === 'TEMPLATE')
			.forEach((c) => {
				// Resolve entity from populated data (object) or cross-reference (number)
				if (c.component) {
					const compId =
						typeof c.component === 'number' ? c.component : (c.component as Component)?.id;
					const compName =
						typeof c.component === 'object'
							? (c.component as Component)?.name
							: componentsData.find((co) => co.id === compId)?.name;
					templates.push({
						condition: c,
						entityId: compId,
						entityName: compName || c.name || `Component #${compId}`,
						level: 'component',
					});
				} else if (c.category) {
					const catId = typeof c.category === 'number' ? c.category : (c.category as Category)?.id;
					const catName =
						typeof c.category === 'object'
							? (c.category as Category)?.name
							: categoriesData.find((ca) => ca.id === catId)?.name;
					templates.push({
						condition: c,
						entityId: catId,
						entityName: catName || c.name || `Category #${catId}`,
						level: 'category',
					});
				} else if (c.expenseType) {
					const etId =
						typeof c.expenseType === 'number' ? c.expenseType : (c.expenseType as ExpenseType)?.id;
					const etName =
						typeof c.expenseType === 'object'
							? (c.expenseType as ExpenseType)?.name
							: expenseTypesData.find((e) => e.id === etId)?.name;
					templates.push({
						condition: c,
						entityId: etId,
						entityName: etName || c.name || `Expense Type #${etId}`,
						level: 'expenseType',
					});
				}
			});

		return templates;
	}, [templateConditionsAll, componentsData, categoriesData, expenseTypesData]);

	// Count templates per level for badges
	const templateCountByLevel = useMemo(() => {
		const counts: Record<ConditionLevel, number> = { component: 0, category: 0, expenseType: 0 };
		allTemplates.forEach((t) => counts[t.level]++);
		return counts;
	}, [allTemplates]);

	// Set of entity IDs that already have templates (for Autocomplete hints)
	const entitiesWithTemplates = useMemo(() => {
		const ids = new Set<number>();
		allTemplates.filter((t) => t.level === selectedLevel).forEach((t) => ids.add(t.entityId));
		return ids;
	}, [allTemplates, selectedLevel]);

	// Group templates by level for the overview
	const templatesByLevel = useMemo(() => {
		const groups: Record<ConditionLevel, typeof allTemplates> = {
			component: [],
			category: [],
			expenseType: [],
		};
		allTemplates.forEach((t) => groups[t.level].push(t));
		return groups;
	}, [allTemplates]);

	return (
		<Box>
			{/* Level selector + entity picker — compact single card */}
			<Paper
				elevation={0}
				sx={{
					...cardStyles.base(theme),
					mb: 2,
					p: spacing.md,
				}}>
				<Stack spacing={2}>
					<RadioGroup
						row
						value={selectedLevel}
						onChange={(e) => setSelectedLevel(e.target.value as ConditionLevel)}>
						{LEVEL_OPTIONS.map((opt) => {
							const count = templateCountByLevel[opt.value];
							return (
								<FormControlLabel
									key={opt.value}
									value={opt.value}
									control={<Radio />}
									label={
										<Stack direction="row" alignItems="center" spacing={1}>
											{React.cloneElement(opt.icon as React.ReactElement, {
												sx: { fontSize: 22, color: opt.iconColor },
											})}
											<span>{opt.label}</span>
											{count > 0 && (
												<Chip
													label={count}
													size="small"
													sx={{
														height: 22,
														minWidth: 22,
														fontSize: 12,
														fontWeight: 700,
														bgcolor: alpha(opt.iconColor, 0.12),
														color: opt.iconColor,
													}}
												/>
											)}
										</Stack>
									}
									sx={{
										mr: 3,
										'& .MuiFormControlLabel-label': { ...typography.body },
									}}
								/>
							);
						})}
					</RadioGroup>
					{isLoadingEntities ? (
						<Skeleton variant="rounded" height={48} sx={{ maxWidth: 480 }} />
					) : (
						<Autocomplete
							options={entityOptions}
							getOptionLabel={(opt) => opt.label}
							value={selectedEntity}
							onChange={(_, val) => setSelectedEntityId(val?.id ?? null)}
							isOptionEqualToValue={(opt, val) => opt.id === val.id}
							renderOption={(props, opt) => (
								<li {...props} key={opt.id}>
									<Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
										<Typography sx={{ ...typography.body, flex: 1 }}>{opt.label}</Typography>
										{entitiesWithTemplates.has(opt.id) && (
											<Chip
												label="Has template"
												size="small"
												sx={{
													height: 22,
													fontSize: 11,
													fontWeight: 600,
													bgcolor: alpha(selectedLevelOption.iconColor, 0.1),
													color: selectedLevelOption.iconColor,
												}}
											/>
										)}
									</Stack>
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder={`Select ${LEVEL_LABEL[selectedLevel].toLowerCase()}...`}
								/>
							)}
							sx={{ maxWidth: 480 }}
						/>
					)}
				</Stack>
			</Paper>

			{/* Selected entity — header + templates in one card */}
			{selectedEntityId && selectedEntity && (
				<Paper
					elevation={0}
					sx={{
						...cardStyles.base(theme),
						p: spacing.md,
					}}>
					<Stack spacing={2}>
						{/* Entity header row */}
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Stack direction="row" alignItems="center" spacing={1.5}>
								<Avatar
									sx={{
										bgcolor: alpha(selectedLevelOption.iconColor, 0.1),
										color: selectedLevelOption.iconColor,
										height: 42,
										width: 42,
									}}>
									{React.cloneElement(selectedLevelOption.icon as React.ReactElement, {
										sx: { fontSize: 22 },
									})}
								</Avatar>
								<Box>
									<Typography sx={{ ...typography.bodyBold }}>{selectedEntity.label}</Typography>
									<Typography sx={{ ...typography.body, color: '#6b7280' }}>
										{templateConditions.length} template
										{templateConditions.length !== 1 ? 's' : ''}
										{documentRequirements.length > 0 &&
											` · ${documentRequirements.length} document req.`}
									</Typography>
								</Box>
							</Stack>
							<Button
								variant="contained"
								startIcon={<IconAdd />}
								onClick={handleStartAddCondition}
								disabled={isAddingCondition}
								sx={{ ...typography.body }}>
								Add Condition
							</Button>
						</Stack>

						{/* Inline add form */}
						{isAddingCondition && (
							<Box
								sx={{
									p: 2,
									borderRadius: radius.sm,
									border: `1px solid ${alpha(GRADIENTS.condition.icon, 0.3)}`,
									bgcolor: alpha(GRADIENTS.condition.icon, 0.02),
								}}>
								<ConditionSettingsEditor
									verificationSections={newConditionSections}
									onChange={setNewConditionSections}
								/>
								<Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
									<Button onClick={handleCancelAddCondition}>Cancel</Button>
									<Button
										variant="contained"
										disabled={newConditionSections.length === 0}
										onClick={handleSaveCondition}>
										Save
									</Button>
								</Stack>
							</Box>
						)}

						{/* Document requirements — compact list */}
						{selectedLevel === 'expenseType' && documentRequirements.length > 0 && (
							<Box>
								<Typography sx={{ ...typography.bodyBold, color: '#6b7280', mb: 1 }}>
									Document Requirements
								</Typography>
								<Stack spacing={0.5}>
									{documentRequirements.map((doc) => (
										<Stack
											key={doc.id}
											direction="row"
											alignItems="center"
											spacing={1}
											sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: '#fefce8' }}>
											<IconDocument sx={{ fontSize: 22, color: '#ca8a04' }} />
											<Typography sx={{ ...typography.body, flex: 1 }}>
												{doc.name || `Document #${doc.id}`}
											</Typography>
											{doc.isRequired && (
												<Typography sx={{ ...typography.body, color: '#dc2626', fontWeight: 600 }}>
													Required
												</Typography>
											)}
										</Stack>
									))}
								</Stack>
							</Box>
						)}

						{/* Template list */}
						{templateConditions.length === 0 && !isAddingCondition ? (
							<Typography sx={{ ...typography.body, color: '#9ca3af', textAlign: 'center', py: 3 }}>
								No condition templates yet — click &quot;Add Condition&quot; to create one.
							</Typography>
						) : (
							<Stack spacing={1}>
								{templateConditions.map((condition, idx) => {
									const sections = condition.verificationSections || [];
									const totalFields = sections.reduce((sum, s) => sum + (s.fields?.length || 0), 0);								const supplierFieldCount = sections.reduce(
									(sum, s) =>
										sum + (s.fields?.filter((f) => f.allowSupplierCompletion).length || 0),
									0
								);									const isEditing = editingConditionId === condition.id;
									return (
										<Box
											key={condition.id}
											sx={{
												px: 2,
												py: 1.25,
												borderRadius: radius.sm,
												border: '1px solid #e5e7eb',
												borderLeft: `3px solid ${selectedLevelOption.iconColor}`,
												transition: 'box-shadow 0.15s',
												'&:hover': { boxShadow: shadows.sm },
											}}>
											<Stack direction="row" alignItems="center" spacing={1.5}>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Stack direction="row" alignItems="center" spacing={1}>
														<Typography sx={{ ...typography.bodyBold }}>
															Template #{idx + 1}
														</Typography>
														<Typography sx={{ ...typography.body, color: '#9ca3af' }}>
															{sections.length} section{sections.length !== 1 ? 's' : ''}
															{' · '}
															{totalFields} field{totalFields !== 1 ? 's' : ''}
														</Typography>
														{supplierFieldCount > 0 && (
															<Chip
																label={`${supplierFieldCount} supplier field${supplierFieldCount !== 1 ? 's' : ''}`}
																size="small"
																sx={{
																	height: 22,
																	bgcolor: '#e3f2fd',
																	color: '#1565c0',
																	fontWeight: 600,
																	'& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
																}}
															/>
														)}
													</Stack>
													{!isEditing && sections.length > 0 && (
														<Stack
															direction="row"
															spacing={0.75}
															sx={{ mt: 0.5, flexWrap: 'wrap' }}>
															{sections.map((s) => (
																<Chip
																	key={s.id}
																	icon={<IconGroup sx={{ fontSize: 20 }} />}
																	label={`${s.name || 'Untitled'} (${s.fields?.length || 0})`}
																	size="small"
																	sx={{
																		...typography.body,
																		bgcolor: '#f3f4f6',
																		height: 32,
																	}}
																/>
															))}
														</Stack>
													)}
												</Box>
												{!isEditing && (
													<>
														<Tooltip title="Edit">
															<IconButton size="small" onClick={() => handleStartEdit(condition)}>
																<IconEdit sx={{ fontSize: 22 }} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Delete">
															<IconButton
																size="small"
																color="error"
																onClick={() => setDeleteConfirmId(condition.id)}>
																<IconDelete sx={{ fontSize: 22 }} />
															</IconButton>
														</Tooltip>
													</>
												)}
											</Stack>
											{/* Inline edit form */}
											{isEditing && (
												<Box
													sx={{
														mt: 1.5,
														p: 2,
														borderRadius: radius.sm,
														border: `1px solid ${alpha(selectedLevelOption.iconColor, 0.3)}`,
														bgcolor: alpha(selectedLevelOption.iconColor, 0.02),
													}}>
													<ConditionSettingsEditor
														verificationSections={editSections}
														onChange={setEditSections}
													/>
													<Stack
														direction="row"
														spacing={1.5}
														justifyContent="flex-end"
														sx={{ mt: 2 }}>
														<Button onClick={handleCancelEdit} sx={{ ...typography.body }}>
															Cancel
														</Button>
														<Button
															variant="contained"
															onClick={handleSaveEdit}
															sx={{ ...typography.body }}>
															Save
														</Button>
													</Stack>
												</Box>
											)}
										</Box>
									);
								})}
							</Stack>
						)}
					</Stack>
				</Paper>
			)}

			{/* Overview of all configured templates — only when no entity selected */}
			{!selectedEntityId && allTemplates.length > 0 && (
				<Paper
					elevation={0}
					sx={{
						...cardStyles.base(theme),
						p: spacing.md,
						mt: 2,
					}}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
						<Typography sx={{ ...typography.bodyBold, color: '#374151' }}>
							Configured Templates
						</Typography>
						<Typography sx={{ ...typography.body, color: '#9ca3af' }}>
							{allTemplates.length} template{allTemplates.length !== 1 ? 's' : ''} configured
						</Typography>
					</Stack>
					<Stack spacing={2.5}>
						{LEVEL_OPTIONS.map((levelOpt) => {
							const group = templatesByLevel[levelOpt.value];
							if (group.length === 0) return null;
							return (
								<Box key={levelOpt.value}>
									<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
										{React.cloneElement(levelOpt.icon as React.ReactElement, {
											sx: { fontSize: 18, color: levelOpt.iconColor },
										})}
										<Typography sx={{ ...typography.body, color: '#6b7280', fontWeight: 600 }}>
											{levelOpt.label}
										</Typography>
										<Chip
											label={group.length}
											size="small"
											sx={{
												height: 20,
												minWidth: 20,
												fontSize: 11,
												fontWeight: 700,
												bgcolor: alpha(levelOpt.iconColor, 0.1),
												color: levelOpt.iconColor,
											}}
										/>
									</Stack>
									<Stack spacing={0.75}>
										{group.map(({ condition, entityId, entityName, level }) => {
											const sections = condition.verificationSections || [];
											const totalFields = sections.reduce(
												(sum, s) => sum + (s.fields?.length || 0),
												0
											);
											const supplierFieldCount = sections.reduce(
												(sum, s) =>
													sum + (s.fields?.filter((f) => f.allowSupplierCompletion).length || 0),
												0
											);
											return (
												<Stack
													key={condition.id}
													direction="row"
													alignItems="center"
													spacing={1.5}
													sx={{
														px: 1.5,
														py: 1,
														borderRadius: radius.sm,
														border: '1px solid #e5e7eb',
														borderLeft: `3px solid ${levelOpt.iconColor}`,
														cursor: 'pointer',
														transition: 'all 0.15s ease',
														'&:hover': {
															bgcolor: alpha(levelOpt.iconColor, 0.03),
															boxShadow: shadows.sm,
														},
													}}
													onClick={() => {
														setSelectedLevel(level);
														setTimeout(() => setSelectedEntityId(entityId), 0);
													}}>
													<Typography sx={{ ...typography.bodyBold, flex: 1 }}>
														{entityName}
													</Typography>
													<Typography sx={{ ...typography.body, color: '#9ca3af' }}>
														{sections.length} section
														{sections.length !== 1 ? 's' : ''} · {totalFields} field
														{totalFields !== 1 ? 's' : ''}
													</Typography>														{supplierFieldCount > 0 && (
															<Chip
																label={`${supplierFieldCount} supplier field${supplierFieldCount !== 1 ? 's' : ''}`}
																size="small"
																sx={{
																	height: 20,
																	bgcolor: '#e3f2fd',
																	color: '#1565c0',
																	fontWeight: 600,
																	'& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
																}}
															/>
														)}												</Stack>
											);
										})}
									</Stack>
								</Box>
							);
						})}
					</Stack>
				</Paper>
			)}

			{/* Empty state — only when nothing selected and no templates exist */}
			{!selectedEntityId && allTemplates.length === 0 && (
				<Typography sx={{ ...typography.body, color: '#9ca3af', textAlign: 'center', py: 4 }}>
					Select a level and entity above to configure verification condition templates.
				</Typography>
			)}

			{/* Deactivate confirmation dialog */}
			<Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
				<DialogTitle sx={{ ...typography.bodyBold }}>Deactivate Condition Template</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ ...typography.body }}>
						Are you sure you want to deactivate this condition template? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmId(null)} disabled={isDeactivating}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleConfirmDeactivate}
						disabled={isDeactivating}>
						{isDeactivating ? 'Deactivating...' : 'Deactivate'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
